import { Injectable, inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, catchError, throwError } from 'rxjs';
import { Message, MessageService } from 'primeng/api';
import { AuthService } from '../auth/auth.service';
import { SKIP_ERROR_TOAST } from '../http/error-toast.context';

const ERROR_MESSAGES: Record<string, string> = {
  INVALID_CREDENTIALS: 'Correo o contraseña incorrectos.',
  ACCOUNT_LOCKED: 'Cuenta bloqueada por múltiples intentos fallidos. Inténtalo en 10 minutos.',
  TENANT_SUSPENDED: 'Tu organización está suspendida. Contacta al administrador.',
  INVALID_REFRESH: 'Sesión expirada. Vuelve a iniciar sesión.',
  SUPER_ADMIN_IP_BLOCKED: 'Acceso denegado desde tu red. Conecta a la VPN corporativa.',
  INVALID_MFA_TOKEN: 'El token MFA expiró o ya fue utilizado. Vuelve a iniciar sesión.',
  INVALID_MFA_CODE: 'Código MFA incorrecto.',
  MFA_CODE_RATE_LIMITED: 'Demasiados intentos MFA. Vuelve a iniciar sesión.',
  MFA_ALREADY_ENROLLED: 'Ya tienes MFA activo en tu cuenta.',
  INVALID_PASSWORD_SETUP_TOKEN: 'El enlace de configuración de contraseña expiró o ya fue usado.',
  PASSWORD_ALREADY_SET: 'La contraseña ya fue configurada. Inicia sesión normalmente.',
  WEAK_PASSWORD: 'La contraseña no cumple los requisitos de seguridad del tenant.',
  SSO_NOT_ALLOWED: 'El proveedor SSO no está habilitado para tu organización.',
  SSO_TENANT_NOT_RESOLVED: 'No se pudo determinar tu organización desde el proveedor SSO.',
  SSO_EMAIL_NOT_VERIFIED: 'Tu email no está verificado en el proveedor SSO.',
  SSO_REDIRECT_URI_NOT_ALLOWED: 'La URI de redirección SSO no está autorizada.',
  SSO_INVALID_NONCE: 'La solicitud SSO expiró. Intenta de nuevo.',
  SSO_PROVIDER_ERROR: 'El proveedor SSO rechazó la autenticación.',
  // Audit
  AUDIT_FORBIDDEN_CROSS_TENANT: 'No tienes permiso para acceder a datos de otros tenants.',
  AUDIT_JUSTIFICATION_REQUIRED:
    'Se requiere justificación para accesos cross-tenant (mínimo 5 caracteres).',
  AUDIT_TENANT_CONTEXT_REQUIRED: 'Tu sesión no tiene contexto de tenant. Vuelve a iniciar sesión.',
  AUDIT_EXPORT_TOO_LARGE:
    'El rango seleccionado supera 100 000 filas. Reduce el período o añade más filtros.',
  AUDIT_LOG_NOT_FOUND: 'El registro de auditoría no existe o ya expiró.',
  AUDIT_JOB_NOT_FOUND: 'El job de exportación no existe o ya expiró.',
  AUDIT_DOWNLOAD_TOKEN_INVALID: 'El enlace de descarga es inválido o expiró.',
  AUDIT_DOWNLOAD_EXPIRED: 'El enlace de descarga expiró (validez 24 h). Genera un nuevo export.',
  // Act-as-tenant (super_admin operando la flota de un tenant)
  ACT_AS_TENANT_REQUIRED:
    'Selecciona un tenant en "Operar como" para gestionar su flota (vehículos/conductores).',
  ACT_AS_JUSTIFICATION_REQUIRED:
    'Se requiere una justificación (mínimo 5 caracteres) para operar como otro tenant.',
  ACT_AS_TENANT_NOT_FOUND: 'El tenant seleccionado no existe o fue eliminado.',
  ACT_AS_TENANT_NOT_OPERABLE:
    'El tenant seleccionado está suspendido o terminado y no es operable.',
  TENANT_CONTEXT_REQUIRED: 'Tu sesión no tiene contexto de tenant. Vuelve a iniciar sesión.',
  // Tenants (alta — back-office)
  // FGA-197: hasta el arreglo del api, este código llegaba también cuando el
  // duplicado era el NIT, así que el mensaje no puede afirmar cuál de los dos es.
  // El alta de tenants ya no depende de este texto —usa el `fix.field` del 409—,
  // pero cualquier otra pantalla que reciba el código sigue cayendo acá.
  SUBDOMAIN_TAKEN: 'Hay un dato repetido: revisa el subdominio y el NIT.',
  /** Choque de unicidad que el api no pudo atribuir a un campo concreto. */
  TENANT_CONFLICT: 'Otro registro tomó uno de estos datos. Revisa el subdominio y el NIT.',
  TAX_ID_TAKEN: 'El NIT/identificación fiscal ya está registrado en otro tenant.',
  // Stations
  RANGE_TOO_LARGE: 'El rango seleccionado supera el límite de 30 días. Reduce el período.',
  NO_TENANT_CONTEXT:
    'No tienes contexto de tenant para esta consulta. Selecciona un tenant (act-as).',
  // Stations
  INVALID_POLYGON: 'El polígono es inválido (auto-intersectante o mal formado).',
  POLYGON_TOO_MANY_VERTICES: 'El polígono tiene demasiados vértices (máximo 10 000).',
  MULTIPLE_POLYGONS_NOT_SUPPORTED: 'Solo se admite un polígono por estación.',
  UNKNOWN_FUEL_TYPE: 'Tipo de combustible no reconocido en el catálogo.',
  STATION_CODE_TAKEN: 'El código de estación ya está en uso.',
  // ADR-0006: pedir una estación de otro país (o dada de baja) devuelve 404.
  STATION_NOT_FOUND: 'La estación no existe o no está disponible.',
  PUMP_NFC_DUPLICATE: 'El NFC tag ya está asignado a otro surtidor.',
  PUMP_CODE_TAKEN: 'El código de surtidor ya existe en esta isla.',
  OVERLAPPING_WINDOWS: 'Hay ventanas horarias que se superponen en el mismo día.',
  INVALID_SCHEDULE_WINDOWS: 'Configuración de horarios inválida.',
  PLACES_UNAVAILABLE: 'El servicio de Google Places no está disponible temporalmente.',
  IMMUTABLE_FIELD: 'No se puede modificar este campo después de la creación.',
  // Fuente de captura de posición (HU position-source)
  GPS_DEVICE_NOT_LINKED:
    'El vehículo no tiene un dispositivo GPS vinculado. Vincula uno o elige "Celular del conductor".',
  // Geofences (FGA-16 / M05)
  INVALID_CATEGORY: 'Tipo de geocerca no válido.',
  INVALID_KML: 'No se pudo leer el archivo KML (formato inválido).',
  INVALID_GEOJSON: 'No se pudo leer el archivo GeoJSON (formato inválido).',
  MULTI_GEOMETRY_NOT_SUPPORTED: 'El archivo contiene varias geometrías; súbelas una por una.',
  UNSUPPORTED_CONTENT_TYPE: 'Solo se admiten archivos .kml o .geojson.',
  PAYLOAD_TOO_LARGE: 'El archivo es demasiado grande.',
  GEOFENCE_NOT_FOUND: 'Geocerca no encontrada.',
  // Credit rules (FGA-17 / M04)
  VALIDATION_ERROR: 'Revisa los campos marcados.',
  FUEL_TYPE_NOT_FOUND: 'Tipo de combustible no válido en la lista.',
  // Sin enumerar monedas: el backend manda la correcta en `expected` y
  // DYNAMIC_ERROR_MESSAGES la usa. Este texto es sólo el respaldo para cuando no
  // viene. Un "(GTQ/CRC)" fijo no dice cuál de las dos era y envejece mal — el día
  // que entre un tercer país miente en toda pantalla que no intercepte el error.
  CURRENCY_MISMATCH: 'La moneda no corresponde a la del cliente.',
  SCOPE_TARGET_NOT_FOUND: 'El grupo o vehículo seleccionado no existe.',
  CREDIT_RULE_NOT_FOUND: 'Regla de crédito no encontrada.',
  // Shifts / transacciones del turno (FGA-30 / M16)
  SHIFT_NOT_OPEN: 'El turno ya no está abierto. Consulta el histórico en el detalle del turno.',
  SHIFT_NOT_FOUND: 'El turno no existe o no es visible para tu estación.',
  STATION_REQUIRED: 'Selecciona una estación para ver sus transacciones.',
  // Despacho / reserva atómica (FGA-19 / M04)
  FUEL_DISPATCH_NOT_FOUND: 'Despacho no encontrado.',
  RESERVATION_NOT_FOUND: 'El despacho no tiene una reserva que liberar.',
  // Dice "reserva" y no "despacho" a proposito: la reserva por vehiculo no crea
  // fuel_dispatch, y este codigo tambien sale del authorize por QR — en los dos caminos
  // lo que bloquea es una reserva activa del conductor.
  DRIVER_HAS_ACTIVE_RESERVATION: 'El conductor ya tiene una reserva en curso.',
  // FGA-32c §3: el antiguo INSUFFICIENT_BALANCE se partió en tres. Este ya es solo
  // "la bolsa no alcanza" — el tope de la regla viaja en CREDIT_LIMIT_EXCEEDED.
  INSUFFICIENT_BALANCE: 'El saldo de crédito de la flota no alcanza para esta operación.',
  NO_APPLICABLE_RULE: 'No hay regla de crédito para este vehículo.',
  // Reserva por vehículo (FGA-32 / M15) + errores explícitos (FGA-32c / M15+M04)
  NO_REFERENCE_PRICE: 'La regla de crédito necesita un precio máximo por unidad.',
  FUEL_TYPE_NOT_ALLOWED: 'La regla de crédito no permite este tipo de combustible.',
  WALLET_NOT_FUNDED: 'La flota nunca recibió crédito. Un SUPER_ADMIN debe hacer una recarga.',
  CREDIT_LIMIT_EXCEEDED: 'La operación supera un tope de la regla de crédito.',
  // Devices / provisioning GPS (FGA-51 / M03)
  IMEI_ALREADY_EXISTS: 'Ya existe un dispositivo con este IMEI.',
  DEVICE_ALREADY_LINKED: 'Este dispositivo ya está vinculado a otro vehículo.',
  VEHICLE_ALREADY_LINKED: 'El vehículo seleccionado ya tiene otro dispositivo.',
  DEVICE_NOT_LINKED: 'El dispositivo no tiene un vínculo que liberar.',
  DEVICE_DEACTIVATED: 'El dispositivo está dado de baja; no admite esta acción.',
  DEVICE_NOT_FOUND: 'Dispositivo no encontrado.',
  VEHICLE_NOT_FOUND: 'Vehículo no encontrado.',
  IDEMPOTENCY_CONFLICT: 'Solicitud duplicada con datos distintos. Recarga e intenta de nuevo.',
  // SIM / conectividad 1GLOBAL (FGA-52 / M03 submódulo SIM)
  SIM_ICCID_ALREADY_EXISTS: 'Este ICCID ya está registrado. Usa otro.',
  DEVICE_ALREADY_HAS_SIM: 'El dispositivo ya tiene una SIM. Edita o elimina la existente.',
  SIM_INVALID_TRANSITION: 'La SIM no admite ese cambio de estado.',
  CONNECTIVITY_PROVIDER_ERROR:
    'El proveedor de conectividad rechazó la operación. La SIM quedó sin cambios.',
  CONNECTIVITY_PROVIDER_UNAVAILABLE:
    'No se pudo contactar al proveedor de conectividad. La SIM quedó sin cambios. Reintenta en un momento.',
  // Config de red del GPS (FGA-53 / M03 submódulo config)
  CONFIG_INCOMPLETE: 'La configuración está incompleta (falta APN); no hay comando que enviar.',
  MSISDN_MISSING: 'La SIM no tiene número (MSISDN); regístralo para poder enviar el SMS.',
  // Neutral: sirve para config (FGA-53) Y comandos (FGA-59).
  PROTOCOL_NOT_SUPPORTED: 'El protocolo del dispositivo no admite esta operación.',
  SMS_PROVIDER_ERROR:
    'No se pudo enviar el SMS: el proveedor lo rechazó. La configuración quedó sin cambios.',
  SMS_PROVIDER_UNAVAILABLE:
    'No se pudo contactar al proveedor de SMS. Sin cambios; reintenta en un momento.',
  CONFIG_ALREADY_APPLIED: 'La configuración ya estaba aplicada.',
  // Comandos remotos al GPS (FGA-59 / M03 submódulo commands)
  COMMAND_NOT_FOUND: 'Comando no encontrado.',
  DEVICE_NOT_COMMANDABLE: 'El dispositivo no admite comandos en su estado actual.',
  COMMAND_TYPE_NOT_SUPPORTED_FOR_PROTOCOL: 'Este comando no aplica a este dispositivo.',
  // Documentos legales (M20 fase 1 / ADR-0008)
  DOCUMENT_NOT_EDITABLE:
    'Solo se pueden editar borradores. Para cambiar un texto publicado, crea una versión nueva.',
  DOCUMENT_VERSION_TAKEN:
    'Ya existe una versión con ese identificador para este tipo, país e idioma.',
  DOCUMENT_NOT_PUBLISHABLE: 'Solo se puede publicar un borrador.',
  DOCUMENT_NOT_ARCHIVABLE: 'Solo se puede archivar una versión publicada.',
  DOCUMENT_NOT_FOUND: 'La versión del documento no existe.',
  DOCUMENT_TYPE_NOT_FOUND: 'El tipo de documento no está en el catálogo.',
  // Permisos (M21 — navegación server-driven)
  INSUFFICIENT_ROLE: 'Tu rol no tiene acceso a esta operación.',
  PERMISSION_DENIED: 'No tienes permiso para esta acción. Consulta con tu administrador.',
  PERMISSION_NOT_DECLARED:
    'Error de configuración del servidor. Intenta más tarde o contacta a soporte.',
};

/**
 * Códigos que NO se toastean globalmente: el componente que hizo la llamada los
 * renderiza con tratamiento propio (estado in-screen). Solo se propaga el error.
 * - `RESERVATION_TERMINAL_STATE` (FGA-19): cancelar un despacho ya terminal es
 *   idempotente → el componente refresca el estado en vez de mostrar error rojo.
 * - Geocerca/telemetría/config (FGA-21): la pantalla de autorización los muestra
 *   como estados bloqueantes accionables ("muévete a la estación" / "espera fix
 *   GPS" / "config rota → soporte"), no como un toast genérico.
 */
const SILENT_ERROR_CODES = new Set<string>([
  'RESERVATION_TERMINAL_STATE',
  'GEOFENCE_OUT_OF_BOUNDS',
  'GPS_PRECISION_INSUFFICIENT',
  'TELEMETRY_STALE',
  'NO_TELEMETRY',
  'STATION_GEOFENCE_MISCONFIGURED',
  'STATION_COUNTRY_MISMATCH',
  'DISPATCH_NOT_AUTHORIZABLE',
  // FGA-32: cancelar una reserva ya cancelada es idempotente → el componente
  // refresca el estado en vez de mostrar un toast rojo.
  'RESERVATION_ALREADY_CANCELED',
  // FGA-32b: vehículo sin conductor asignado → el form de reserva lo muestra
  // in-screen con aviso + CTA a asignar conductor (evita el toast genérico 422).
  'NO_ACTIVE_DRIVER_ASSIGNMENT',
  // FGA-28 (apertura de turno): manejados in-screen por el portal de estación
  // (open-shift.component.onError → banner/aviso por fila o redirect), evitando
  // el doble toast (CA-06/CA-07).
  'SHIFT_ALREADY_OPEN',
  'MISSING_FUEL_PRICE',
  'FUEL_TYPE_NOT_DISPATCHED',
  'MISSING_INITIAL_READING',
  'INVALID_DISPATCHER',
  'DISPATCHER_DUPLICATE_ISLAND',
  'FUEL_PRICE_CONFLICT',
  // FGA-28b (cierre de turno): manejados in-screen por close-shift.onError
  // (mensaje específico por bomba faltante / no monótona), evitan el doble toast.
  'MISSING_FINAL_READING',
  'READING_NOT_MONOTONIC',
  // FGA-52: device sin SIM → el panel lo renderiza como estado vacío con CTA
  // "Registrar SIM" (no un toast rojo).
  'SIM_NOT_FOUND',
  // FGA-53: device sin config viva → el panel lo renderiza como estado vacío con
  // CTA "Generar configuración" (no un toast rojo).
  'CONFIG_NOT_FOUND',
]);

/**
 * Mensajes que NO se pueden resolver con un texto fijo porque dependen del cuerpo
 * del error. Devolver `undefined` deja que gane el texto de `ERROR_MESSAGES`.
 *
 * Existe por FGA-160/161: el portal mostraba "(GTQ/CRC)" mientras el backend le
 * estaba diciendo cuál era la moneda correcta en `expected`, y la tiraba.
 */
const DYNAMIC_ERROR_MESSAGES: Record<
  string,
  (body: Record<string, unknown>) => string | undefined
> = {
  CURRENCY_MISMATCH: (body) => {
    const expected = typeof body['expected'] === 'string' ? body['expected'] : null;
    if (!expected) return undefined;
    const received = typeof body['received'] === 'string' ? body['received'] : null;
    return received
      ? `La moneda del cliente es ${expected}. Se envió ${received}.`
      : `La moneda del cliente es ${expected}.`;
  },
};

@Injectable({ providedIn: 'root' })
export class ErrorInterceptor implements HttpInterceptor {
  private readonly toast = inject(MessageService);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);

  /**
   * Firma de toast → instante en que deja de estar en pantalla.
   *
   * Sin esto, cada respuesta fallida agregaba SU toast aunque el mensaje fuera
   * idéntico al que ya se estaba mostrando, y el stack tapaba la pantalla. El
   * caso que lo destapó: un SUPER_ADMIN sin "Operar como" recibe el mismo 403
   * `ACT_AS_TENANT_REQUIRED` en cada pantalla de flota, así que navegar entre
   * ellas apilaba seis avisos iguales (uno por vista, cada uno vivo 10 s).
   *
   * El dedupe es por CONTENIDO, no por endpoint: dos errores distintos que le
   * dicen exactamente lo mismo al usuario son, para él, un solo aviso. Repetir
   * el mensaje no agrega información; lo único que agrega es ruido.
   */
  private readonly toastShownUntil = new Map<string, number>();

  /**
   * Muestra el toast salvo que uno idéntico siga vivo. La ventana es el propio
   * `life` del mensaje: apenas desaparece de pantalla, el mismo error vuelve a
   * poder avisar (si el usuario reintenta y falla otra vez, se entera).
   */
  private showToast(msg: Message & { life: number }): void {
    const now = Date.now();
    // Barrido oportunista: el mapa no acumula firmas ya vencidas.
    for (const [k, until] of this.toastShownUntil) {
      if (until <= now) this.toastShownUntil.delete(k);
    }

    const key = `${msg.severity}|${msg.summary}|${msg.detail}`;
    if ((this.toastShownUntil.get(key) ?? 0) > now) return;

    this.toastShownUntil.set(key, now + msg.life);
    this.toast.add(msg);
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((rawErr: unknown) => {
        const err = rawErr as HttpErrorResponse;
        if (req.url.includes('/health')) return throwError(() => rawErr);

        // 401 — gestionado por AuthInterceptor; no mostrar toast duplicado
        if (err.status === 401) return throwError(() => rawErr);

        const code: string | undefined = err.error?.code;
        // Los dinámicos ganan cuando el cuerpo trae lo que necesitan; si no, cae al
        // texto fijo. Ver `DYNAMIC_ERROR_MESSAGES`.
        const customMsg = code
          ? (DYNAMIC_ERROR_MESSAGES[code]?.(err.error ?? {}) ?? ERROR_MESSAGES[code])
          : undefined;

        // M21: el endpoint no declara permiso. Es un bug del backend, no una falta
        // de acceso del usuario — se reporta para que quede rastro, y el toast
        // genérico evita culpar a quien no tiene nada que ver.
        if (code === 'PERMISSION_NOT_DECLARED') {
          console.error(`[permisos] ${req.method} ${req.url} no declara permiso (bug de backend)`);
        }

        // M21: el catálogo de `.write` del portal no está cubierto por el gate de CI
        // del backend, así que puede ser más generoso que lo que la API realmente
        // exige. Cuando divergen, `requiredPermission` es el único dato que dice qué
        // permiso pidió la API — se deja rastro para poder ajustar el catálogo.
        if (code === 'PERMISSION_DENIED') {
          console.warn(
            `[permisos] ${req.method} ${req.url} exige '${err.error?.requiredPermission}'`,
          );
        }

        // Códigos silenciosos (idempotencia, etc.): el componente los maneja.
        if (code && SILENT_ERROR_CODES.has(code)) return throwError(() => rawErr);

        // Opt-in por petición: la pantalla renderiza el error in-screen (ver
        // `error-toast.context.ts` para el detalle de los dos alcances).
        const skipToast = req.context.get(SKIP_ERROR_TOAST);
        const isBusinessStatus = [400, 404, 409, 422].includes(err.status);
        if (skipToast === 'business' && isBusinessStatus) {
          return throwError(() => rawErr);
        }
        if (skipToast === 'all' && err.status !== 403) {
          return throwError(() => rawErr);
        }

        if (err.status === 403) {
          if (code === 'SUPER_ADMIN_IP_BLOCKED') {
            // Forzar cierre de sesión local sin llamar al backend (evitar loop)
            this.auth.clearLocalSession();
            this.showToast({
              severity: 'error',
              summary: 'Acceso bloqueado',
              detail: ERROR_MESSAGES['SUPER_ADMIN_IP_BLOCKED'],
              life: 10000,
            });
            void this.router.navigate(['/login']);
          } else if (code === 'TENANT_SUSPENDED') {
            // Limpiar sesión y redirigir a pantalla dedicada
            this.auth.clearLocalSession();
            void this.router.navigate(['/account-suspended']);
          } else {
            this.showToast({
              severity: 'error',
              summary: 'Acceso denegado',
              detail:
                customMsg ?? err.error?.message ?? 'No tienes permiso para acceder a este recurso.',
              life: 10000,
            });
          }
        } else if (err.status === 423) {
          this.showToast({
            severity: 'warn',
            summary: 'Cuenta bloqueada',
            detail: customMsg ?? ERROR_MESSAGES['ACCOUNT_LOCKED'],
            life: 10000,
          });
        } else if (err.status === 409) {
          this.showToast({
            severity: 'warn',
            summary: 'No se pudo completar',
            detail: customMsg ?? err.error?.message ?? 'La operación no se pudo completar.',
            life: 6000,
          });
        } else if (err.status === 422) {
          this.showToast({
            severity: 'warn',
            summary: 'Acción no permitida',
            detail: customMsg ?? err.error?.message ?? 'Operación no válida.',
            life: 6000,
          });
        } else if (err.status === 429) {
          // Llega aquí solo si ThrottleInterceptor ya reintentó y sigue fallando
          const retryAfter = err.headers.get('Retry-After') ?? '60';
          this.showToast({
            severity: 'warn',
            summary: 'Demasiadas solicitudes',
            detail: `Límite de peticiones excedido. Inténtalo en ${retryAfter}s.`,
            life: 8000,
          });
        } else if (err.status === 503) {
          this.showToast({
            severity: 'error',
            summary: 'Servicio no disponible',
            detail:
              customMsg ?? err.error?.message ?? 'El servicio no está disponible temporalmente.',
            life: 6000,
          });
        } else if (err.status >= 400) {
          this.showToast({
            severity: err.status >= 500 ? 'error' : 'warn',
            summary: `Error ${err.status}`,
            detail: customMsg ?? err.error?.message ?? err.message,
            life: 5000,
          });
        }

        return throwError(() => rawErr);
      }),
    );
  }
}

/** Solo para pruebas: permite verificar el texto que ve el usuario por código. */
export const ERROR_MESSAGES_FOR_TEST = ERROR_MESSAGES;
